import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ButtonControl, Loading } from "spark-core-dx/components";
import { useAuthContext } from "spark-core-dx/contexts";
import { AuthManager } from "spark-core-dx/managers";
import { getAuthSessionRequestHeaders } from "../../utilities/authSessionRequest";
import "./HardLogout.scss";

const AUTH_LOCAL_STORAGE_KEYS = [
  "authProvider",
  "CoreAuthSession",
  "authguard_registerationinfo"
];

const AUTH_LOCAL_STORAGE_KEY_PATTERNS = [
  /^msal\./i,
  /auth/i,
  /token/i,
  /oidc/i
];

const removeMatchingLocalStorage = () => {
  const keysToRemove = [];

  for (let i = 0; i < window.localStorage.length; i += 1) {
    const key = window.localStorage.key(i);
    if (!key) continue;

    if (
      AUTH_LOCAL_STORAGE_KEYS.includes(key) ||
      AUTH_LOCAL_STORAGE_KEY_PATTERNS.some((pattern) => pattern.test(key))
    ) {
      keysToRemove.push(key);
    }
  }

  keysToRemove.forEach((key) => window.localStorage.removeItem(key));
};

const clearLoginState = () => {
  AuthManager.removeTokensFromStorage();
  window.sessionStorage.clear();
  removeMatchingLocalStorage();
};

const revokeServerSession = async () => {
  const statusResponse = await window.fetch("/api/Core/AuthSession", {
    method: "GET",
    credentials: "include",
    cache: "no-store",
    headers: getAuthSessionRequestHeaders()
  });

  if (!statusResponse.ok && statusResponse.status !== 401) {
    return false;
  }

  const status = statusResponse.ok ? await statusResponse.json() : null;
  const csrfToken = status?.csrfToken;

  const deleteResponse = await window.fetch("/api/Core/AuthSession", {
    method: "DELETE",
    credentials: "include",
    cache: "no-store",
    headers: getAuthSessionRequestHeaders(
      csrfToken ? { "X-Convoke-CSRF": csrfToken } : {}
    )
  });

  if (!deleteResponse.ok) {
    return false;
  }

  const verificationResponse = await window.fetch("/api/Core/AuthSession", {
    method: "GET",
    credentials: "include",
    cache: "no-store",
    headers: getAuthSessionRequestHeaders()
  });

  return verificationResponse.status === 401;
};

const replaceDocument = (path) => window.location.replace(path);

const HardLogout = ({ navigateToSignIn = replaceDocument }) => {
  const navigate = useNavigate();
  const { dispatch } = useAuthContext();
  const [signOutComplete, setSignOutComplete] = useState(false);
  const [serverCleanupConfirmed, setServerCleanupConfirmed] = useState(true);

  useEffect(() => {
    let mounted = true;

    const signOut = async () => {
      let cleanupConfirmed = false;
      try {
        cleanupConfirmed = await revokeServerSession();
      } catch {
        cleanupConfirmed = false;
      } finally {
        clearLoginState();
        dispatch?.({ type: "LOGOUT" });
        if (mounted) {
          setServerCleanupConfirmed(cleanupConfirmed);
          setSignOutComplete(true);
        }
      }
    };

    signOut();
    return () => {
      mounted = false;
    };
  }, [dispatch]);

  return (
    <div className="platform-logout">
      <header className="platform-logout__header">
        <div className="platform-logout__brand" aria-label="Convoke DX">
          <span>CONVOKE</span><span>DX</span>
        </div>
        <div className="platform-logout__header-status">Logged out</div>
      </header>

      <main className="platform-logout__main">
        {!signOutComplete ? (
          <div className="platform-logout__state" role="status">
            <div className="platform-logout__loading">
              <Loading type="spinner-2" size="2rem" />
            </div>
            <h1>Signing you out...</h1>
            <p>We’re securely ending your Convoke session.</p>
          </div>
        ) : (
          <div className="platform-logout__state">
            <p className="platform-logout__eyebrow">Signed out</p>
            <h1>You’re signed out</h1>
            <p>
              Your Convoke session has ended. You can sign in again when you’re
              ready or return to the public homepage.
            </p>

            {!serverCleanupConfirmed && (
              <p className="platform-logout__warning" role="alert">
                Your browser sign-in was cleared, but server session cleanup
                could not be confirmed. It is safe to close this tab.
              </p>
            )}

            <div className="platform-logout__actions">
              <ButtonControl
                type="okay"
                onClick={() => navigateToSignIn("/login/")}
              >
                Sign in again
              </ButtonControl>
              <ButtonControl
                type="standard"
                onClick={() => navigate("/")}
              >
                Return to homepage
              </ButtonControl>
            </div>

            <p className="platform-logout__close-note">
              You may also safely close this browser tab.
            </p>
          </div>
        )}
      </main>
    </div>
  );
};

export default HardLogout;
